/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import IconDots from '@assets/icons/dots.svg?react';
import {
 Avatar,
 Box,
 Button,
 CircularProgress,
 IconButton,
 Menu,
 MenuItem,
 TextField,
 Theme,
 Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useConfirmationDialog } from '../../../contexts/useConfirmationDialog';
import {
 addCommentStart,
 CommentsType,
 deleteCommentStart,
 getCommentsStart,
 updateCommentStart,
} from '../../../redux/comments/commentsSlice';
import { RootState } from '../../../redux/store';
import { titleStyles } from '../../common/InfoSidebar/infoStyles';

const CommentInput = ({
 value,
 setValue,
}: {
 value: string;
 setValue: (value: string) => void;
}) => {
 const { t } = useTranslation();
 return (
  <TextField
   id="comment-input"
   multiline
   minRows={3}
   placeholder={t('gridSidebar.whiteComment')}
   sx={{
    '& fieldset': { border: 'none' },
    width: '100%',
    background: (theme: Theme) => theme.palette.kemek.lightBlue,
    '& textarea': {
     pb: 5,
    },
   }}
   value={value}
   onChange={(e) => setValue(e.target.value)}
  />
 );
};

const InfoComments = ({ rowId }: { rowId: number }) => {
 const dispatch = useDispatch();
 const { t } = useTranslation();
 const { openDialog: openConfirmationDialog } = useConfirmationDialog();
 const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
 const commentsSelector = useSelector((state: RootState) => state.comments);
 const { comments, loading, success } = commentsSelector;
 const [comment, setComment] = useState('');
 const [currentId, setCurrentId] = useState<number | undefined>(undefined);
 const [currentComment, setCurrentComment] = useState<CommentsType | undefined>(
  undefined,
 );

 const getComments = () => {
  dispatch(
   getCommentsStart({
    id: rowId,
    table: 'Result',
   }),
  );
 };

 useEffect(() => {
  if (!rowId) return;
  getComments();
 }, [dispatch, rowId]);

 useEffect(() => {
  if (success?.addComment || success?.updateComment || success?.deleteComment) {
   getComments();
   setComment('');
   setCurrentId(undefined);
   setCurrentComment(undefined);
  }
 }, [success]);

 const handleSubmit = () => {
  if (currentId) {
   dispatch(
    updateCommentStart({
     id: currentId,
     Text: comment,
    }),
   );
  } else {
   dispatch(
    addCommentStart({
     Table: 'Result',
     TableRecordId: rowId,
     Text: comment,
    }),
   );
  }
 };

 const handleDelete = (id: number) => {
  openConfirmationDialog({
   title: t('global.delete'),
   message: t('global.deleteMessage'),
   onConfirm: () => {
    dispatch(deleteCommentStart({ id }));
   },
  });
 };

 return (
  <>
   <Typography sx={titleStyles}>Komentarai</Typography>
   <Box
    sx={{
     //  p: 2,
     display: 'flex',
     gap: 2,
     flexDirection: 'column',
     mb: 3,
    }}
   >
    {loading?.getComments ? (
     <CircularProgress />
    ) : !comments ? (
     'Šiuo metu komentarų nėra.'
    ) : (
     comments?.map((c: CommentsType) => (
      <Box
       key={`comment_${c.Id}`}
       sx={{
        background: (theme: Theme) =>
         c.Id === currentId ? theme.palette.kemek.lightBlue : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
       }}
      >
       <Box
        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}
       >
        <Avatar
         sx={{
          background: 'transparent',
          border: (theme: Theme) =>
           `1px solid ${theme.palette.kemek.lightestGray}`,
          color: (theme: Theme) => theme.palette.kemek.gray,
          fontWeight: 500,
          fontSize: 16,
         }}
        >
         {c.UserFullName?.trim()
          .split(/\s+/)
          .map((word) => word[0].toUpperCase())
          .join('')}
        </Avatar>
        <Box>
         <Typography sx={{ fontSize: 12 }}>{c.UserFullName}</Typography>
         <Typography
          sx={{
           fontSize: 10,
           color: (theme: Theme) => theme.palette.kemek.gray,
          }}
         >
          {c.Modified
           ? dayjs(c.Modified).format('YYYY-MM-DD')
           : dayjs(c.Created).format('YYYY-MM-DD')}
         </Typography>
        </Box>
        <Box sx={{ ml: 'auto' }}>
         <IconButton
          aria-label="menu"
          color="primary"
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
           setAnchorEl(e.currentTarget);
           setCurrentComment(c);
          }}
         >
          <IconDots />
         </IconButton>
         <Menu
          MenuListProps={{ disablePadding: true }}
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={() => {
           setAnchorEl(null);
           setCurrentComment(undefined);
          }}
         >
          <MenuItem
           onClick={() => {
            setComment(currentComment?.Text || 'wtf');
            setCurrentId(currentComment?.Id);
            setAnchorEl(null);
           }}
          >
           Redaguoti
          </MenuItem>
          <MenuItem
           sx={{ color: (theme: Theme) => theme.palette.kemek.red }}
           onClick={() => {
            if (currentComment) {
             handleDelete(currentComment?.Id);
             setAnchorEl(null);
            }
           }}
          >
           Ištrinti
          </MenuItem>
         </Menu>
        </Box>
       </Box>
       <Typography sx={{ fontSize: 12 }}>{c.Text}</Typography>
      </Box>
     ))
    )}
   </Box>
   <Box sx={{ position: 'relative' }}>
    <CommentInput value={comment} setValue={setComment} />
    <Box
     sx={{
      display: 'flex',
      gap: 1,
      position: 'absolute',
      bottom: '12px',
      right: '12px',
     }}
    >
     {currentId && (
      <Button
       variant="outlined"
       color="error"
       type="button"
       onClick={() => {
        setComment('');
        setCurrentId(undefined);
       }}
      >
       {t('global.cancel')}
      </Button>
     )}
     <Button
      variant="contained"
      type="button"
      onClick={() => handleSubmit()}
      disabled={!comment}
     >
      {t('global.send')}
     </Button>
    </Box>
   </Box>
  </>
 );
};

export default InfoComments;
